import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { BieHistoricalWorkbenchModel } from './bieHistorical.models';

export type HistoricalGapResolutionMode = 'UNSET' | 'FORECAST';

type Props = {
  workbench: BieHistoricalWorkbenchModel;
  gapResolutionMode: HistoricalGapResolutionMode;
  onOpenImports: () => void;
  onOpenWorkbook: () => void;
  onOpenManualEntry: () => void;
  onOpenCoverage: () => void;
  onUseForecasting: () => void;
};

const percent = (covered: number, expected: number): number => {
  if (expected <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((covered / expected) * 100)));
};

const CoverageBullet = ({
  label,
  covered,
  expected,
}: {
  label: string;
  covered: number;
  expected: number;
}) => {
  const missing = expected > 0 ? Math.max(0, expected - covered) : 0;
  const value = percent(covered, expected);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {expected > 0 ? `${covered} of ${expected}` : `${covered} available · target not configured`}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={expected > 0 ? value : covered > 0 ? 100 : 0}
        sx={{ mt: 0.75, height: 10, borderRadius: 5 }}
      />
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {expected > 0 ? `${value}% covered` : 'Repository observation'}
        </Typography>
        <Typography variant="caption" color={missing > 0 ? 'warning.main' : 'text.secondary'}>
          {expected > 0 ? `${missing} gap${missing === 1 ? '' : 's'}` : 'Set the expected boundary during validation'}
        </Typography>
      </Stack>
    </Box>
  );
};

const MetricCard = ({ label, value, note }: { label: string; value: number; note: string }) => (
  <Grid size={{ xs: 6, md: 3 }}>
    <Paper variant="outlined" sx={{ p: 1.75, height: '100%' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.25 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{note}</Typography>
    </Paper>
  </Grid>
);

class HistoricalOverviewPanel extends React.PureComponent<Props> {
  render() {
    const { workbench } = this.props;
    const { summary, coverage, readiness, validation, datasets } = workbench;
    const hasFacts = summary.factcount > 0;
    const hasConfiguredTargets =
      coverage.expectedyears > 0 ||
      coverage.expectedperiods > 0 ||
      coverage.expectedmetrics > 0 ||
      coverage.expectedproducts > 0;
    const knownGapCount =
      coverage.missingyearcount +
      coverage.missingperiodcount +
      coverage.missingmetriccount +
      coverage.missingproductcount;

    const readinessLabel = readiness.complete
      ? 'Certified'
      : readiness.blockingcount > 0
        ? 'Blocked'
        : validation.validatedat
          ? knownGapCount > 0 ? 'Partial — gaps remain' : 'Validated'
          : hasFacts ? 'Assessment pending' : 'No historical baseline';

    const readinessColor = readiness.complete
      ? 'success'
      : readiness.blockingcount > 0
        ? 'error'
        : validation.validatedat
          ? 'warning'
          : 'default';

    const latestDataset = datasets?.[0];

    return (
      <Stack spacing={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <AssessmentIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Historical Readiness Overview</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Review what history is available, identify the remaining planning gaps, then add actuals, controlled estimates or forecast the eligible remainder.
              </Typography>
            </Box>
            <Chip color={readinessColor as any} label={readinessLabel} sx={{ fontWeight: 700 }} />
          </Stack>

          <Grid container spacing={1.5} sx={{ mt: 0.75 }}>
            <MetricCard label="Datasets" value={summary.datasetcount} note="Recognisable contribution batches" />
            <MetricCard label="Imports" value={summary.importcount} note="Published source contributions" />
            <MetricCard label="Historical Facts" value={summary.factcount} note="Available repository observations" />
            <MetricCard label="Metrics" value={summary.metriccount || coverage.coveredmetrics} note="Business variables represented" />
          </Grid>
        </Paper>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Coverage against the Planning Cycle boundary</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Coverage is descriptive before validation and becomes authoritative after the Planning Cycle boundary is validated.
              </Typography>
              <Stack spacing={2}>
                <CoverageBullet label="Years" covered={coverage.coveredyears} expected={coverage.expectedyears} />
                <CoverageBullet label="Periods" covered={coverage.coveredperiods} expected={coverage.expectedperiods} />
                <CoverageBullet label="Metrics" covered={coverage.coveredmetrics} expected={coverage.expectedmetrics} />
                <CoverageBullet label="Products" covered={coverage.coveredproducts} expected={coverage.expectedproducts} />
              </Stack>
              {!hasConfiguredTargets && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Some expected boundaries are not yet configured. Validation should derive them from the Planning Cycle before gaps are treated as complete or forecastable.
                </Alert>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Repository snapshot</Typography>
              <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Latest Dataset</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {latestDataset?.name || 'No published dataset'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Coverage Window</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {latestDataset
                      ? [latestDataset.coveragefrom, latestDataset.coverageto].filter(Boolean).join(' — ') || 'Not supplied'
                      : 'Not available'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Origin</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {latestDataset?.planningcycleid
                      ? 'Planning Cycle'
                      : latestDataset?.loadsessionid
                        ? 'Onboarding'
                        : 'Repository contribution'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Active Facts</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{latestDataset?.activefactcount ?? summary.factcount}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Resolve the historical boundary</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Use actual historical information whenever it exists. For the remainder, explicitly choose controlled forecasting only after eligible gaps have been assessed.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
            <Button startIcon={<CloudUploadIcon />} variant="outlined" onClick={this.props.onOpenImports}>
              Review Existing Imports
            </Button>
            <Button startIcon={<CloudUploadIcon />} variant="outlined" onClick={this.props.onOpenWorkbook} disabled={!workbench.actions.canimport}>
              Import Additional Actuals
            </Button>
            <Button startIcon={<EditNoteIcon />} variant="outlined" onClick={this.props.onOpenManualEntry} disabled={!workbench.actions.canmanualentry}>
              Add Manual Facts
            </Button>
            <Button
              startIcon={<AutoGraphIcon />}
              variant={this.props.gapResolutionMode === 'FORECAST' ? 'contained' : 'outlined'}
              onClick={this.props.onUseForecasting}
            >
              Use Forecasting for Remaining Gaps
            </Button>
            <Button startIcon={<FactCheckIcon />} variant="outlined" onClick={this.props.onOpenCoverage}>
              Validate Coverage
            </Button>
          </Stack>

          {this.props.gapResolutionMode === 'FORECAST' && (
            <Alert severity="info" icon={<AutoGraphIcon />} sx={{ mt: 2 }}>
              Forecasting has been selected for the remaining eligible gaps. Internal gaps may require interpolation or controlled estimation; trailing gaps may be forecast; unsupported leading or sparse gaps must remain visible for manual resolution or an external source.
            </Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Recommended workflow</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review readiness → add available actuals or manual facts → choose forecasting for eligible gaps → validate the baseline → complete and continue to Forecasting.
          </Typography>
          <Button endIcon={<ArrowForwardIcon />} sx={{ mt: 1 }} onClick={this.props.onOpenCoverage}>
            Review Coverage & Integrity
          </Button>
        </Paper>
      </Stack>
    );
  }
}

export default HistoricalOverviewPanel;
